/**
 * Education Scope admin (Admin ▸ Enablement ▸ Education Scope).
 *
 * Curates which Mindtickle series COUNT toward a learner's required-education metric. Mindtickle's own
 * INVITE_TYPE / MODULE_RELEVANCE are unreliable for this org, so an admin confirms per-series scope into
 * the source-of-truth `EducationScopeEntry` list. Three sections:
 *   - Reconcile: every distinct series assigned across the org, with a rule suggestion + current entry;
 *     the admin toggles in_scope / required / window / program label per series and saves in bulk.
 *   - Confirmed entries: the persisted source of truth, with delete.
 *   - Rules: convenience first-match-wins draft rules that seed the reconcile suggestions.
 *
 * All data comes from toolbox_snowflake (admin-gated). The heavy reconcile draft hits Snowflake, so it is
 * only fetched when its tab is active. A global-proxy admin can re-root the org via the Root email field.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';

import {
  EDUCATION_WINDOW_OPTIONS,
  EducationWindow,
  ScopeCandidate,
  ScopeRuleInput,
  ScopeSort,
  useConfirmScope,
  useDeleteEntry,
  useDeleteRule,
  useScopeDraft,
  useScopeEntries,
  useScopeRules,
  useUpsertRule,
} from '../../../hooks/education-scope-hooks';

type SubTab = 'reconcile' | 'entries' | 'rules';

/** Compact local date-time for the created/updated columns; em-dash when never curated. */
const fmtDateTime = (s: string | null | undefined) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

/** Editable scope fields tracked per series while the admin works the reconcile grid. */
interface DraftEdit {
  in_scope: boolean;
  required: boolean;
  window: EducationWindow;
  program_label: string;
}

const windowLabel = (w: EducationWindow) =>
  EDUCATION_WINDOW_OPTIONS.find((o) => o.value === w)?.label ?? w;

/** Seed an edit row from the current entry, else the rule suggestion, else out-of-scope defaults. */
const seedEdit = (c: ScopeCandidate): DraftEdit => {
  const src = c.entry ?? c.suggested;
  return {
    in_scope: src?.in_scope ?? false,
    required: src?.required ?? false,
    window: (src?.window as EducationWindow) ?? 'current_fiscal_year',
    program_label: src?.program_label ?? '',
  };
};

const isLocalModeError = (err: unknown) =>
  err instanceof Error && /local\/demo mode/i.test(err.message);

const EducationScopeAdminPage: React.FC = () => {
  const [tab, setTab] = useState<SubTab>('reconcile');

  return (
    <Box>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Choose which Mindtickle series count toward each learner&apos;s required-education metric. Entries
        you confirm here are the source of truth; rules only pre-fill the reconcile grid.
      </Typography>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label='Reconcile' value='reconcile' />
        <Tab label='Confirmed entries' value='entries' />
        <Tab label='Rules' value='rules' />
      </Tabs>

      {tab === 'reconcile' && <ReconcileTab active />}
      {tab === 'entries' && <EntriesTab active />}
      {tab === 'rules' && <RulesTab active />}
    </Box>
  );
};

// ── Reconcile grid ─────────────────────────────────────────────────────────

const ReconcileTab: React.FC<{ active: boolean }> = ({ active }) => {
  const [rootEmail, setRootEmail] = useState('');
  const [appliedRoot, setAppliedRoot] = useState('');
  const [onlyDrafted, setOnlyDrafted] = useState(false);
  const [sort, setSort] = useState<ScopeSort>('updated');
  const { data, isLoading, isError, error, refetch, isFetching } = useScopeDraft(
    appliedRoot || undefined,
    onlyDrafted,
    active,
    sort,
  );
  const confirm = useConfirmScope();

  // Local edits keyed on series_id; re-seeded whenever a fresh draft arrives.
  const [edits, setEdits] = useState<Record<string, DraftEdit>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState<number | null>(null);

  const candidates = useMemo(() => data?.candidates ?? [], [data]);

  useEffect(() => {
    const seeded: Record<string, DraftEdit> = {};
    candidates.forEach((c) => {
      seeded[c.series_id] = seedEdit(c);
    });
    setEdits(seeded);
    setDirty(new Set());
  }, [candidates]);

  const patch = (seriesId: string, delta: Partial<DraftEdit>) => {
    setEdits((prev) => ({ ...prev, [seriesId]: { ...prev[seriesId], ...delta } }));
    setDirty((prev) => new Set(prev).add(seriesId));
    setSaved(null);
  };

  const onSave = async () => {
    const rows = Array.from(dirty).map((seriesId) => {
      const c = candidates.find((x) => x.series_id === seriesId);
      const e = edits[seriesId];
      return {
        series_id: seriesId,
        series_name: c?.series_name,
        in_scope: e.in_scope,
        required: e.required,
        window: e.window,
        program_label: e.program_label,
        source: 'manual' as const,
      };
    });
    if (!rows.length) return;
    const res: any = await confirm.mutateAsync(rows);
    setSaved(res?.upserted ?? rows.length);
    setDirty(new Set());
  };

  if (isError) {
    return (
      <Alert severity={isLocalModeError(error) ? 'info' : 'error'}>
        {isLocalModeError(error)
          ? 'Series reconcile is unavailable in local/demo mode — it reads live Mindtickle data from Snowflake.'
          : `Could not load series: ${(error as Error)?.message ?? 'unknown error'}`}
      </Alert>
    );
  }

  const columns: GridColDef<ScopeCandidate>[] = [
    {
      field: 'series_name',
      headerName: 'Series',
      flex: 2,
      minWidth: 240,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant='body2'>{p.row.series_name || '(unnamed)'}</Typography>
          <Typography variant='caption' color='text.secondary'>
            {p.row.series_id}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'suggested',
      headerName: 'Suggested',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (p) =>
        p.row.suggested ? (
          <Tooltip title={p.row.suggested.rule_label ? `Rule: ${p.row.suggested.rule_label}` : ''}>
            <Chip
              size='small'
              color={p.row.suggested.required ? 'primary' : 'default'}
              label={p.row.suggested.required ? 'Required' : p.row.suggested.in_scope ? 'In scope' : 'Out'}
            />
          </Tooltip>
        ) : (
          <Typography variant='caption' color='text.secondary'>
            —
          </Typography>
        ),
    },
    {
      field: 'in_scope',
      headerName: 'In scope',
      width: 100,
      sortable: false,
      renderCell: (p) => (
        <Checkbox
          size='small'
          checked={edits[p.row.series_id]?.in_scope ?? false}
          onChange={(e) =>
            patch(p.row.series_id, {
              in_scope: e.target.checked,
              ...(e.target.checked ? {} : { required: false }),
            })
          }
        />
      ),
    },
    {
      field: 'required',
      headerName: 'Required',
      width: 100,
      sortable: false,
      renderCell: (p) => (
        <Checkbox
          size='small'
          disabled={!edits[p.row.series_id]?.in_scope}
          checked={edits[p.row.series_id]?.required ?? false}
          onChange={(e) => patch(p.row.series_id, { required: e.target.checked })}
        />
      ),
    },
    {
      field: 'window',
      headerName: 'Window',
      width: 180,
      sortable: false,
      renderCell: (p) => (
        <FormControl size='small' fullWidth>
          <Select
            value={edits[p.row.series_id]?.window ?? 'current_fiscal_year'}
            onChange={(e) => patch(p.row.series_id, { window: e.target.value as EducationWindow })}
          >
            {EDUCATION_WINDOW_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: 'program_label',
      headerName: 'Program label',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (p) => (
        <TextField
          size='small'
          fullWidth
          value={edits[p.row.series_id]?.program_label ?? ''}
          onChange={(e) => patch(p.row.series_id, { program_label: e.target.value })}
        />
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Last updated',
      width: 180,
      // Server default order is updated-desc; this column shows when (and lets the admin see
      // never-curated series as "—"). created_at is surfaced as a caption.
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant='body2'>{fmtDateTime(p.row.updated_at)}</Typography>
          {p.row.created_at && (
            <Typography variant='caption' color='text.secondary'>
              created {fmtDateTime(p.row.created_at)}
            </Typography>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
        <TextField
          size='small'
          label='Root email (org to draft)'
          placeholder='defaults to you'
          value={rootEmail}
          onChange={(e) => setRootEmail(e.target.value)}
          helperText='Global-proxy admins can draft another org (e.g. kyturner@cisco.com)'
          sx={{ minWidth: 300 }}
        />
        <Button variant='outlined' onClick={() => setAppliedRoot(rootEmail.trim())}>
          Load
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              size='small'
              checked={onlyDrafted}
              onChange={(e) => setOnlyDrafted(e.target.checked)}
            />
          }
          label='Only rule-matched / already-curated'
        />
        <FormControl size='small' sx={{ minWidth: 190 }}>
          <Select value={sort} onChange={(e) => setSort(e.target.value as ScopeSort)}>
            <MenuItem value='updated'>Most recently updated</MenuItem>
            <MenuItem value='name'>Series name (A–Z)</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title='Reload from Snowflake'>
          <span>
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant='contained'
          startIcon={<SaveIcon />}
          disabled={dirty.size === 0 || confirm.isPending}
          onClick={onSave}
        >
          {confirm.isPending ? 'Saving…' : `Save ${dirty.size || ''} change${dirty.size === 1 ? '' : 's'}`}
        </Button>
      </Stack>

      {saved != null && <Alert severity='success'>Saved {saved} scope {saved === 1 ? 'entry' : 'entries'}.</Alert>}
      {confirm.isError && (
        <Alert severity='error'>Save failed: {(confirm.error as Error)?.message ?? 'unknown error'}</Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant='outlined'>
          <DataGrid
            autoHeight
            rows={candidates}
            columns={columns}
            getRowId={(r) => r.series_id}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Paper>
      )}
      <Typography variant='caption' color='text.secondary'>
        {data ? `${data.candidate_count} of ${data.series_count} series shown.` : ''}
      </Typography>
    </Stack>
  );
};

// ── Confirmed entries ──────────────────────────────────────────────────────

const EntriesTab: React.FC<{ active: boolean }> = ({ active }) => {
  const { data, isLoading, isError, error } = useScopeEntries(active);
  const del = useDeleteEntry();
  const entries = data?.entries ?? [];

  if (isError) {
    return (
      <Alert severity={isLocalModeError(error) ? 'info' : 'error'}>
        {isLocalModeError(error)
          ? 'Confirmed entries are unavailable in local/demo mode.'
          : `Could not load entries: ${(error as Error)?.message ?? 'unknown error'}`}
      </Alert>
    );
  }

  const columns: GridColDef[] = [
    { field: 'series_name', headerName: 'Series', flex: 2, minWidth: 220 },
    { field: 'series_id', headerName: 'Series ID', flex: 1, minWidth: 140 },
    {
      field: 'in_scope',
      headerName: 'In scope',
      width: 100,
      renderCell: (p) => (p.row.in_scope ? <Chip size='small' color='success' label='Yes' /> : <Chip size='small' label='No' />),
    },
    {
      field: 'required',
      headerName: 'Required',
      width: 100,
      renderCell: (p) => (p.row.required ? <Chip size='small' color='primary' label='Required' /> : <span>—</span>),
    },
    { field: 'window', headerName: 'Window', width: 170, valueFormatter: (v) => windowLabel(v as EducationWindow) },
    { field: 'program_label', headerName: 'Program', flex: 1, minWidth: 120 },
    { field: 'source', headerName: 'Source', width: 90 },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      valueFormatter: (v) => fmtDateTime(v as string | null),
    },
    {
      field: 'updated_at',
      headerName: 'Updated',
      width: 170,
      valueFormatter: (v) => fmtDateTime(v as string | null),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title='Remove entry'>
          <IconButton
            size='small'
            disabled={del.isPending}
            onClick={() => del.mutate({ id: p.row.id })}
          >
            <DeleteOutlineIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      {del.isError && (
        <Alert severity='error'>Delete failed: {(del.error as Error)?.message ?? 'unknown error'}</Alert>
      )}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : entries.length === 0 ? (
        <Alert severity='info'>No confirmed scope entries yet. Use the Reconcile tab to curate series.</Alert>
      ) : (
        <Paper variant='outlined'>
          <DataGrid
            autoHeight
            rows={entries}
            columns={columns}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Paper>
      )}
    </Stack>
  );
};

// ── Rules ──────────────────────────────────────────────────────────────────

const EMPTY_RULE: ScopeRuleInput = {
  label: '',
  include: [],
  exclude: [],
  required: true,
  window: 'current_fiscal_year',
  program_label: '',
  active: true,
  order: 0,
};

const RulesTab: React.FC<{ active: boolean }> = ({ active }) => {
  const { data, isLoading, isError, error } = useScopeRules(active);
  const upsert = useUpsertRule();
  const del = useDeleteRule();
  const [form, setForm] = useState<ScopeRuleInput>(EMPTY_RULE);
  const rules = data?.rules ?? [];

  const csv = (s: string) =>
    s.split(',').map((x) => x.trim()).filter(Boolean);

  const onSubmit = async () => {
    if (!form.label.trim()) return;
    await upsert.mutateAsync(form);
    setForm(EMPTY_RULE);
  };

  if (isError) {
    return (
      <Alert severity={isLocalModeError(error) ? 'info' : 'error'}>
        {isLocalModeError(error)
          ? 'Rules are unavailable in local/demo mode.'
          : `Could not load rules: ${(error as Error)?.message ?? 'unknown error'}`}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper variant='outlined' sx={{ p: 2 }}>
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          {form.id ? 'Edit rule' : 'New rule'}
        </Typography>
        <Stack spacing={2}>
          <Stack direction='row' spacing={2} flexWrap='wrap'>
            <TextField
              size='small'
              label='Label'
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size='small'
              label='Include (comma-separated substrings)'
              value={form.include.join(', ')}
              onChange={(e) => setForm({ ...form, include: csv(e.target.value) })}
              sx={{ minWidth: 260 }}
            />
            <TextField
              size='small'
              label='Exclude (comma-separated)'
              value={form.exclude.join(', ')}
              onChange={(e) => setForm({ ...form, exclude: csv(e.target.value) })}
              sx={{ minWidth: 220 }}
            />
          </Stack>
          <Stack direction='row' spacing={2} flexWrap='wrap' alignItems='center'>
            <FormControl size='small' sx={{ minWidth: 180 }}>
              <Select
                value={form.window}
                onChange={(e) => setForm({ ...form, window: e.target.value as EducationWindow })}
              >
                {EDUCATION_WINDOW_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size='small'
              label='Program label'
              value={form.program_label}
              onChange={(e) => setForm({ ...form, program_label: e.target.value })}
            />
            <TextField
              size='small'
              type='number'
              label='Order'
              value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
              sx={{ width: 100 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={!!form.required}
                  onChange={(e) => setForm({ ...form, required: e.target.checked })}
                />
              }
              label='Required'
            />
            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={!!form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
              }
              label='Active'
            />
            <Box sx={{ flexGrow: 1 }} />
            {form.id && (
              <Button onClick={() => setForm(EMPTY_RULE)}>Cancel</Button>
            )}
            <Button
              variant='contained'
              disabled={!form.label.trim() || upsert.isPending}
              onClick={onSubmit}
            >
              {form.id ? 'Update rule' : 'Add rule'}
            </Button>
          </Stack>
          {upsert.isError && (
            <Alert severity='error'>Save failed: {(upsert.error as Error)?.message ?? 'unknown error'}</Alert>
          )}
        </Stack>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : rules.length === 0 ? (
        <Alert severity='info'>No rules yet. Rules pre-fill the reconcile grid; they are optional.</Alert>
      ) : (
        <Stack spacing={1}>
          {rules.map((r) => (
            <Paper key={r.id} variant='outlined' sx={{ p: 1.5 }}>
              <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                <Typography variant='subtitle2'>{r.label}</Typography>
                {r.required && <Chip size='small' color='primary' label='Required' />}
                {!r.active && <Chip size='small' label='Inactive' />}
                <Chip size='small' variant='outlined' label={windowLabel(r.window)} />
                {r.program_label && <Chip size='small' variant='outlined' label={r.program_label} />}
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant='caption' color='text.secondary'>
                  order {r.order}
                </Typography>
                <Button
                  size='small'
                  onClick={() =>
                    setForm({
                      id: r.id,
                      label: r.label,
                      include: r.include,
                      exclude: r.exclude,
                      required: r.required,
                      window: r.window,
                      program_label: r.program_label,
                      active: r.active,
                      order: r.order,
                    })
                  }
                >
                  Edit
                </Button>
                <IconButton size='small' disabled={del.isPending} onClick={() => del.mutate(r.id)}>
                  <DeleteOutlineIcon fontSize='small' />
                </IconButton>
              </Stack>
              <Typography variant='caption' color='text.secondary'>
                include: {r.include.join(', ') || '—'} · exclude: {r.exclude.join(', ') || '—'}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default EducationScopeAdminPage;
